import { MutateOptions } from '@tanstack/react-query';

/**
 * @remarks
 * Create/Delete Modal Group Message
 * **/
export const MAX_GROUP_NAME_LENGTH = 64;
export const MAX_GROUP_NAME_ERROR_MESSAGE = `Please provide a name under ${MAX_GROUP_NAME_LENGTH} characters`;
export const UNKNOWN_ERROR_MESSAGE = 'Some error occured. Please try again later.';

/**
 * @remarks
 * Action Bar Group Message
 * **/
export const GROUP_PGS_LIMIT_MESSAGE = (currentDataType: string) => `Make a group of up to 150 ${currentDataType}`;
export const PRODUCT_GROUP_MAX_SELECTION = 150;
export const MODEL_GROUP_PGS_LIMIT_MESSAGE = 'Make a group of up to 30 model groups';
export const MODEL_GROUP_MAX_SELECTION = 30;
export const EXPLORE_LEADERBOARD_TABLE = 'Explore Leaderboard Table';
export const OPPORTUNITY_REVIEW_BOARD_TABLE = 'OpportunityReview Table';
export const totalNumberSelectedForItems = (totalItemsSelected: number, totalItems: number, currentDataType: string) =>
  `${totalItemsSelected} of ${totalItems} ${currentDataType}(s) selected`;
export const totalNumberSelectedForAddingItems = (
  editingGroupName: string,
  totalItemsSelected: number,
  itemsMaxSelection: number,
) => `${editingGroupName} - ${totalItemsSelected} Item(s) selected - out of ${itemsMaxSelection}`;

/**
 * @remarks
 * Amplitude Tracking for Group element name and type
 * **/
export enum CREATE_GROUP_ELEMENT_NAME {
  category = 'Create Category Group',
  model = 'Create Model Group',
}
export enum ADD_PGS_ELEMENT_NAME {
  category = 'Add Category Group',
  model = 'Add Model Group',
}
export enum DELETE_GROUP_PG_ELEMENT_NAME {
  category = 'Delete Category Group',
  model = 'Delete Model Group',
}
export enum GROUP_ELEMENT_TYPE {
  category = 'Table Category Group',
  model = 'Table Model Group',
}

export enum PreferenceResponseStatus {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
}

export interface Preference<T> {
  id: string;
  namespace: string;
  key: string;
  type: string;
  values: T;
  isPreferenceOwner: boolean;
}

export interface PreferenceContext {
  type: string;
  namespace: string;
  key: string;
}

export interface PreferenceResponse<T> {
  message: string;
  status: PreferenceResponseStatus;
  values?: Preference<T>;
}

export interface ErrorStatusResponse {
  statusCode: number;
  message: string;
}

export interface DeletePreferenceResponse {
  message: string;
  status: PreferenceResponseStatus;
}

export interface DeletePreferencePermissionResponse {
  message: string;
  status: PreferenceResponseStatus;
}

export interface AddPreferencePermissionResponse {
  message: string;
  status: PreferenceResponseStatus;
}

export interface PreferenceRequest<T> {
  key: string;
  data: T;
}

export interface DeletePreferenceRequest {
  key: string;
}

export interface DeletePreferencePermissionRequest {
  key: string;
  userId: string;
}

export interface AddPreferencePermissionRequest {
  key: string;
  permissionBody: any;
}

export type PreferenceMutateOptions<T> = MutateOptions<PreferenceResponse<T>, unknown, PreferenceRequest<T>>;

export type DeletePreferencesMutateOptions = MutateOptions<DeletePreferenceResponse, unknown, unknown>;

export type DeletePreferencesPermissionMutateOptions = MutateOptions<
  DeletePreferencePermissionResponse,
  unknown,
  unknown
>;

export type AddPreferencesPermissionMutateOptions = MutateOptions<AddPreferencePermissionResponse, unknown, unknown>;

export enum PREFERENCE_TYPE {
  categoryLeaderboardGroup = 'category-leaderboard-group',
  productAnalysisGroup = 'product-analysis-group',
  featureLeaderboardGroup = 'feature-leaderboard-group',
  opportunityLeaderboardGroup = 'opportunity-leaderboard-group',
}

export interface CategoryLeaderBoardGroupPreference extends BaseLeaderboardGroupPreference {
  productGroup: string[];
}

export type ProductAnalyzerGroupPreference = {
  modalGroup: string[];
  sortOrder?: number;
  clientId?: number;
};

export interface FeatureLeaderBoardGroupPreference extends BaseLeaderboardGroupPreference {
  value: string[];
}

export interface OpportunityReviewBoardGroupPreference extends BaseLeaderboardGroupPreference {
  groupElement?: string[];
  productGroup?: string[];
  value?: string[];
}

export type BaseLeaderboardGroupPreference = {
  showPerformanceWithin?: string;
  sortOrder?: number;
  clientId?: number;
};

export interface GroupContext {
  client: string;
  country: string;
  productGroup?: string;
  isCategory?: boolean;
}
